import React, { useEffect, useState } from 'react';
import { getSdrAppointments, contractTypeOf, type RdvRow } from '../../lib/refonteApi';
import type { LifecycleStatus } from '../../lib/appointmentLifecycle';
import StatusBadge from '../../components/refonte/StatusBadge';
import ContractBadge from '../../components/refonte/ContractBadge';
import { Skeleton } from '../../components/refonte/Skeleton';
import EmptyState from '../../components/refonte/EmptyState';
import { REFONTE_STRINGS as S } from '../../lib/refonteStrings';

// Colonnes du kanban = regroupement métier des statuts du cycle de vie.
// Lecture seule : aucune transition ici (le moteur en base reste seul juge).
const COLUMNS: Array<{ key: string; title: string; statuses: LifecycleStatus[] }> = [
  { key: 'a-statuer',    title: S.kanban.colAStatuer,   statuses: ['posed', 'confirmed'] },
  { key: 'effectues',    title: S.kanban.colEffectues,  statuses: ['held'] },
  { key: 'qualifies',    title: S.kanban.colQualifies,  statuses: ['qualified', 'not_qualified'] },
  { key: 'facturation',  title: S.kanban.colFacturation, statuses: ['validated', 'billed'] },
];

const KanbanPage: React.FC = () => {
  const [rows, setRows] = useState<RdvRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try { setRows(await getSdrAppointments()); }
      catch (e: any) { setErr(e.message); }
      setLoading(false);
    })();
  }, []);

  return (
    <div>
      <h1 className="text-[21px] font-bold tracking-tight">{S.kanban.title}</h1>
      <p className="text-dim text-[13px] mb-5">
        {rows.length} RDV · {S.common.readOnly} · {S.kanban.subtitle}
      </p>

      {err && <EmptyState tone="error" title="Impossible de charger le kanban" hint={err} className="mb-4" />}

      <div className="grid grid-cols-1 min-[900px]:grid-cols-2 min-[1280px]:grid-cols-4 gap-4 items-start">
        {COLUMNS.map(col => {
          const cards = rows.filter(r => r.lifecycle_status != null && col.statuses.includes(r.lifecycle_status));
          return (
            <section key={col.key} className="bg-panel-2 border border-line rounded-card p-3">
              <h2 className="font-mono text-[10.5px] uppercase tracking-widest text-dim mb-3 flex items-center justify-between">
                {col.title}
                <span className="text-faint">{cards.length}</span>
              </h2>
              <div role="list" aria-label={col.title} className="flex flex-col gap-2">
                {loading && (
                  <>
                    <Skeleton className="h-[72px]" />
                    <Skeleton className="h-[72px]" />
                  </>
                )}
                {cards.map(rdv => (
                  <article role="listitem" key={rdv.id} className="bg-panel border border-line rounded-lg p-3">
                    <div className="text-[13px] font-semibold truncate">
                      {rdv.prospect_name || 'Prospect'}
                    </div>
                    {rdv.company_name && (
                      <div className="text-[11px] text-dim truncate">{rdv.company_name}</div>
                    )}
                    <div className="font-mono text-[9.5px] text-faint mt-1 truncate">
                      {rdv.clients?.name || '—'} · {rdv.date || 'sans date'}
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5 mt-2">
                      <StatusBadge status={rdv.lifecycle_status ?? null} />
                      <ContractBadge contractType={contractTypeOf(rdv)} />
                    </div>
                  </article>
                ))}
                {!loading && cards.length === 0 && (
                  <p className="text-[11.5px] text-faint py-2">{S.kanban.columnEmpty}</p>
                )}
              </div>
            </section>
          );
        })}
      </div>
    </div>
  );
};

export default KanbanPage;
